import { useState, useMemo } from "react";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);

function buildSchedule(loanAmount, annualRate, termYears, startDate, extraPayment, frequency) {
  const principal = parseFloat(loanAmount) || 0;
  const rate = parseFloat(annualRate) / 100;
  const years = parseFloat(termYears) || 0;
  const extra = parseFloat(extraPayment) || 0;

  if (principal <= 0 || rate <= 0 || years <= 0) return [];

  const periodsPerYear = frequency === "biweekly" ? 26 : 12;
  const periodRate = rate / periodsPerYear;
  const totalPeriods = years * periodsPerYear;

  const basePayment =
    (principal * periodRate * Math.pow(1 + periodRate, totalPeriods)) /
    (Math.pow(1 + periodRate, totalPeriods) - 1);

  const rows = [];
  let balance = principal;
  let date = new Date(startDate + "T00:00:00");
  let totalInterestPaid = 0;
  let totalPrincipalPaid = 0;
  let paymentNum = 0;

  while (balance > 0.005 && paymentNum < totalPeriods + 1) {
    paymentNum++;
    const interestCharge = balance * periodRate;
    let principalPayment = basePayment - interestCharge + extra;
    principalPayment = Math.min(principalPayment, balance);
    const actualPayment = interestCharge + principalPayment;
    balance = Math.max(balance - principalPayment, 0);
    totalInterestPaid += interestCharge;
    totalPrincipalPaid += principalPayment;

    rows.push({
      num: paymentNum,
      date: date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      payment: actualPayment,
      principal: principalPayment,
      interest: interestCharge,
      extra,
      balance,
      totalInterestPaid,
      totalPrincipalPaid,
    });

    if (balance <= 0.005) break;

    // Advance date
    if (frequency === "biweekly") {
      date = new Date(date.getTime() + 14 * 24 * 60 * 60 * 1000);
    } else {
      date = new Date(date.getFullYear(), date.getMonth() + 1, date.getDate());
    }
  }

  return rows;
}

function exportCSV(rows, loanAmount, annualRate, termYears, frequency) {
  const headers = ["#", "Date", "Payment", "Principal", "Interest", "Extra", "Balance", "Total Interest Paid"];
  const csvRows = [
    [`ELDE Amortization Schedule`],
    [`Loan Amount: ${formatCurrency(loanAmount)}`, `Rate: ${annualRate}%`, `Term: ${termYears} years`, `Frequency: ${frequency}`],
    [],
    headers,
    ...rows.map((r) => [
      r.num, r.date,
      r.payment.toFixed(2), r.principal.toFixed(2),
      r.interest.toFixed(2), r.extra.toFixed(2),
      r.balance.toFixed(2), r.totalInterestPaid.toFixed(2),
    ]),
  ];
  const csv = csvRows.map((r) => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "ELDE_Amortization_Schedule.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function exportPDF(rows, loanAmount, annualRate, termYears, startDate, frequency, extra) {
  // Build a printable HTML page and trigger print-to-PDF
  const totalInterest = rows[rows.length - 1]?.totalInterestPaid || 0;
  const totalPaid = parseFloat(loanAmount) + totalInterest;
  const payoffDate = rows[rows.length - 1]?.date || "";

  const tableRows = rows.map((r, i) =>
    `<tr style="background:${i % 2 === 0 ? "#f9f9f9" : "#fff"}">
      <td>${r.num}</td><td>${r.date}</td>
      <td>$${r.payment.toFixed(2)}</td>
      <td>$${r.principal.toFixed(2)}</td>
      <td>$${r.interest.toFixed(2)}</td>
      ${extra > 0 ? `<td>$${r.extra.toFixed(2)}</td>` : ""}
      <td>$${r.balance.toFixed(2)}</td>
    </tr>`
  ).join("");

  const html = `
    <html><head><title>ELDE Amortization Schedule</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 12px; margin: 40px; color: #1a1a2e; }
      h1 { font-size: 22px; color: #1a1a2e; margin-bottom: 4px; }
      .subtitle { color: #888; font-size: 13px; margin-bottom: 20px; }
      .summary { display: flex; gap: 24px; margin-bottom: 24px; flex-wrap: wrap; }
      .summary-card { background: #f8f9fc; border-radius: 8px; padding: 12px 16px; min-width: 140px; }
      .summary-label { font-size: 10px; color: #999; text-transform: uppercase; margin-bottom: 4px; }
      .summary-value { font-size: 16px; font-weight: 700; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      th { background: #1a1a2e; color: #fff; padding: 8px 10px; text-align: left; font-size: 10px; text-transform: uppercase; }
      td { padding: 6px 10px; border-bottom: 1px solid #eee; }
      @media print { body { margin: 20px; } }
    </style></head><body>
    <h1>ELDE — Amortization Schedule</h1>
    <div class="subtitle">Generated ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
    <div class="summary">
      <div class="summary-card"><div class="summary-label">Loan Amount</div><div class="summary-value">${formatCurrency(loanAmount)}</div></div>
      <div class="summary-card"><div class="summary-label">Interest Rate</div><div class="summary-value">${annualRate}%</div></div>
      <div class="summary-card"><div class="summary-label">Term</div><div class="summary-value">${termYears} years</div></div>
      <div class="summary-card"><div class="summary-label">Frequency</div><div class="summary-value" style="text-transform:capitalize">${frequency}</div></div>
      ${extra > 0 ? `<div class="summary-card"><div class="summary-label">Extra Payment</div><div class="summary-value">${formatCurrency(extra)}</div></div>` : ""}
      <div class="summary-card"><div class="summary-label">Total Interest</div><div class="summary-value" style="color:#EF4444">${formatCurrency(totalInterest)}</div></div>
      <div class="summary-card"><div class="summary-label">Total Paid</div><div class="summary-value">${formatCurrency(totalPaid)}</div></div>
      <div class="summary-card"><div class="summary-label">Payoff Date</div><div class="summary-value">${payoffDate}</div></div>
    </div>
    <table>
      <thead><tr>
        <th>#</th><th>Date</th><th>Payment</th><th>Principal</th><th>Interest</th>
        ${extra > 0 ? "<th>Extra</th>" : ""}
        <th>Balance</th>
      </tr></thead>
      <tbody>${tableRows}</tbody>
    </table>
    </body></html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 500);
}

export default function Amortization({ onBack }) {
  const [loanAmount, setLoanAmount] = useState("25000");
  const [annualRate, setAnnualRate] = useState("7.5");
  const [termYears, setTermYears] = useState("5");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [extraPayment, setExtraPayment] = useState("0");
  const [frequency, setFrequency] = useState("monthly");
  const [showAll, setShowAll] = useState(false);

  const schedule = useMemo(
    () => buildSchedule(loanAmount, annualRate, termYears, startDate, extraPayment, frequency),
    [loanAmount, annualRate, termYears, startDate, extraPayment, frequency]
  );

  const totalInterest = schedule[schedule.length - 1]?.totalInterestPaid || 0;
  const totalPaid = (parseFloat(loanAmount) || 0) + totalInterest;
  const payoffDate = schedule[schedule.length - 1]?.date || "—";
  const totalPayments = schedule.length;
  const extra = parseFloat(extraPayment) || 0;

  // Comparison without extra payment
  const scheduleNoExtra = useMemo(
    () => extra > 0 ? buildSchedule(loanAmount, annualRate, termYears, startDate, 0, frequency) : [],
    [loanAmount, annualRate, termYears, startDate, frequency, extra]
  );
  const interestWithoutExtra = scheduleNoExtra[scheduleNoExtra.length - 1]?.totalInterestPaid || 0;
  const interestSaved = extra > 0 ? Math.max(interestWithoutExtra - totalInterest, 0) : 0;
  const paymentsSaved = extra > 0 ? Math.max(scheduleNoExtra.length - schedule.length, 0) : 0;

  const displayRows = showAll ? schedule : schedule.slice(0, 24);

  return (
    <div style={styles.page}>

      <main style={styles.main}>
        <button style={styles.backBtn} onClick={onBack}>← Back to Dashboard</button>
        <div style={styles.pageTitle}>📅 Amortization Schedule Builder</div>
        <div style={styles.pageSubtitle}>Build a full payment schedule for any loan</div>

        {/* Inputs */}
        <div style={styles.inputCard}>
          <div style={styles.inputGrid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Loan Amount ($)</label>
              <div style={styles.inputWrap}>
                <span style={styles.affix}>$</span>
                <input style={styles.input} type="number" min="0" value={loanAmount} onChange={(e) => setLoanAmount(e.target.value)} />
              </div>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Annual Interest Rate (%)</label>
              <div style={styles.inputWrap}>
                <input style={{ ...styles.input, borderRight: "none" }} type="number" min="0" value={annualRate} onChange={(e) => setAnnualRate(e.target.value)} />
                <span style={styles.suffixAffix}>%</span>
              </div>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Loan Term (years)</label>
              <div style={styles.inputWrap}>
                <input style={{ ...styles.input, borderRight: "none" }} type="number" min="0" value={termYears} onChange={(e) => setTermYears(e.target.value)} />
                <span style={styles.suffixAffix}>yrs</span>
              </div>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Start Date</label>
              <input style={styles.standaloneInput} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Extra Monthly Payment ($)</label>
              <div style={styles.inputWrap}>
                <span style={styles.affix}>$</span>
                <input style={styles.input} type="number" min="0" value={extraPayment} onChange={(e) => setExtraPayment(e.target.value)} />
              </div>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Payment Frequency</label>
              <div style={styles.toggleRow}>
                <button
                  style={{ ...styles.toggleBtn, ...(frequency === "monthly" ? styles.toggleBtnActive : {}) }}
                  onClick={() => setFrequency("monthly")}
                >Monthly</button>
                <button
                  style={{ ...styles.toggleBtn, ...(frequency === "biweekly" ? styles.toggleBtnActive : {}) }}
                  onClick={() => setFrequency("biweekly")}
                >Bi-weekly</button>
              </div>
            </div>
          </div>
        </div>

        {schedule.length > 0 && (
          <>
            {/* Summary Cards */}
            <div style={styles.summaryGrid}>
              <div style={{ ...styles.summaryCard, borderTop: "3px solid #4F7FFA" }}>
                <div style={styles.summaryLabel}>Loan Amount</div>
                <div style={{ ...styles.summaryValue, color: "#4F7FFA" }}>{formatCurrency(parseFloat(loanAmount) || 0)}</div>
              </div>
              <div style={{ ...styles.summaryCard, borderTop: "3px solid #EF4444" }}>
                <div style={styles.summaryLabel}>Total Interest</div>
                <div style={{ ...styles.summaryValue, color: "#EF4444" }}>{formatCurrency(totalInterest)}</div>
              </div>
              <div style={{ ...styles.summaryCard, borderTop: "3px solid #F97316" }}>
                <div style={styles.summaryLabel}>Total Amount Paid</div>
                <div style={{ ...styles.summaryValue, color: "#F97316" }}>{formatCurrency(totalPaid)}</div>
              </div>
              <div style={{ ...styles.summaryCard, borderTop: "3px solid #10B981" }}>
                <div style={styles.summaryLabel}>Payoff Date</div>
                <div style={{ ...styles.summaryValue, color: "#10B981", fontSize: 18 }}>{payoffDate}</div>
              </div>
              <div style={{ ...styles.summaryCard, borderTop: "3px solid #A855F7" }}>
                <div style={styles.summaryLabel}>Total Payments</div>
                <div style={{ ...styles.summaryValue, color: "#A855F7" }}>{totalPayments}</div>
              </div>
            </div>

            {/* Extra Payment Savings Banner */}
            {extra > 0 && interestSaved > 0 && (
              <div style={styles.savingsBanner}>
                💡 Your extra <strong>{formatCurrency(extra)}/{frequency === "biweekly" ? "payment" : "mo"}</strong> saves{" "}
                <strong style={{ color: "#10B981" }}>{formatCurrency(interestSaved)}</strong> in interest and pays off the loan{" "}
                <strong style={{ color: "#10B981" }}>{paymentsSaved} payments sooner!</strong>
              </div>
            )}

            {/* Export Buttons */}
            <div style={styles.exportRow}>
              <div style={styles.exportLabel}>Export Schedule:</div>
              <button
                style={styles.exportBtn}
                onClick={() => exportCSV(schedule, loanAmount, annualRate, termYears, frequency)}
              >
                📥 Download Excel/CSV
              </button>
              <button
                style={{ ...styles.exportBtn, background: "#EF4444" }}
                onClick={() => exportPDF(schedule, loanAmount, annualRate, termYears, startDate, frequency, extra)}
              >
                🖨️ Print / Save as PDF
              </button>
            </div>

            {/* Schedule Table */}
            <div style={styles.tableCard}>
              <div style={styles.tableHeader}>
                <div>
                  <div style={styles.tableTitle}>Full Payment Schedule</div>
                  <div style={styles.tableSubtitle}>
                    {totalPayments} {frequency === "biweekly" ? "bi-weekly" : "monthly"} payments
                    {" "}· Showing {displayRows.length} of {totalPayments}
                  </div>
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {["#", "Date", "Payment", "Principal", "Interest", ...(extra > 0 ? ["Extra"] : []), "Balance", "Total Interest Paid"].map((h) => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, i) => (
                      <tr key={row.num} style={i % 2 === 0 ? styles.trEven : {}}>
                        <td style={styles.td}>{row.num}</td>
                        <td style={styles.td}>{row.date}</td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{formatCurrency(row.payment)}</td>
                        <td style={{ ...styles.td, color: "#4F7FFA" }}>{formatCurrency(row.principal)}</td>
                        <td style={{ ...styles.td, color: "#EF4444" }}>{formatCurrency(row.interest)}</td>
                        {extra > 0 && <td style={{ ...styles.td, color: "#10B981" }}>{formatCurrency(row.extra)}</td>}
                        <td style={{ ...styles.td, fontWeight: 600 }}>{formatCurrency(row.balance)}</td>
                        <td style={{ ...styles.td, color: "#888" }}>{formatCurrency(row.totalInterestPaid)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {schedule.length > 24 && (
                <div style={styles.showMoreRow}>
                  <button style={styles.showMoreBtn} onClick={() => setShowAll((s) => !s)}>
                    {showAll ? `▲ Show less` : `▼ Show all ${totalPayments} payments`}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#F8F9FC", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#1a1a2e" },
  header: { background: "#1a1a2e", padding: "0 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.15)" },
  headerInner: { maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", height: 64 },
  logo: { fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: 3 },
  logoSub: { fontSize: 11, color: "#8899BB", letterSpacing: 1, marginTop: 2, marginLeft: 10 },
  main: { maxWidth: 1100, margin: "0 auto", padding: "32px 24px" },
  backBtn: { background: "none", border: "1.5px solid #ddd", borderRadius: 8, padding: "8px 16px", fontSize: 14, cursor: "pointer", color: "#555", marginBottom: 24, fontWeight: 500 },
  pageTitle: { fontSize: 26, fontWeight: 800, marginBottom: 6 },
  pageSubtitle: { fontSize: 14, color: "#888", marginBottom: 28 },
  inputCard: { background: "#fff", borderRadius: 14, padding: "24px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", marginBottom: 24 },
  inputGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: "#444" },
  inputWrap: { display: "flex", alignItems: "center", border: "1.5px solid #E5E7EB", borderRadius: 8, overflow: "hidden" },
  affix: { padding: "0 10px", background: "#F8F9FC", color: "#888", fontSize: 14, fontWeight: 600, borderRight: "1px solid #E5E7EB", display: "flex", alignItems: "center", height: 42 },
  suffixAffix: { padding: "0 10px", background: "#F8F9FC", color: "#888", fontSize: 14, fontWeight: 600, borderLeft: "1px solid #E5E7EB", display: "flex", alignItems: "center", height: 42 },
  input: { border: "none", padding: "10px 12px", fontSize: 14, outline: "none", flex: 1 },
  standaloneInput: { border: "1.5px solid #E5E7EB", borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none", width: "100%", boxSizing: "border-box" },
  toggleRow: { display: "flex", gap: 8 },
  toggleBtn: { flex: 1, padding: "9px", borderRadius: 8, border: "1.5px solid #E5E7EB", background: "#F3F4F6", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#555" },
  toggleBtnActive: { background: "#EEF2FF", borderColor: "#4F7FFA", color: "#4F7FFA" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 16, marginBottom: 20 },
  summaryCard: { background: "#fff", borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" },
  summaryLabel: { fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 },
  summaryValue: { fontSize: 22, fontWeight: 800 },
  savingsBanner: { background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 16px", fontSize: 13, color: "#166534", lineHeight: 1.6, marginBottom: 20 },
  exportRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" },
  exportLabel: { fontSize: 13, fontWeight: 600, color: "#555" },
  exportBtn: { background: "#4F7FFA", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer" },
  tableCard: { background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", overflow: "hidden" },
  tableHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "20px 24px 16px" },
  tableTitle: { fontSize: 16, fontWeight: 700, marginBottom: 4 },
  tableSubtitle: { fontSize: 13, color: "#888" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", borderBottom: "2px solid #F0F0F5", background: "#FAFAFA" },
  td: { padding: "10px 14px", borderBottom: "1px solid #F8F9FC" },
  trEven: { background: "#FAFAFA" },
  showMoreRow: { padding: "16px", textAlign: "center", borderTop: "1px solid #F0F0F5" },
  showMoreBtn: { background: "none", border: "1.5px solid #ddd", borderRadius: 8, padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#555" },
};
